/**
 * API client for the Monte Carlo simulation server.
 *
 * All responses are wrapped in envelopes following REST API design rules:
 * - Single resources: { "data": { ... } }
 * - Collections: { "data": [ ... ] }
 * - Errors: { "error": { "code": "...", "message": "...", "details": [...], "trace_id": "..." } }
 */

import type {
  OrganizationMonteCarloRequest,
  OrganizationMonteCarloResponse,
  SimulationSummary,
  SimulationResponse,
  CreateSimulationRequest,
  UpdateSimulationRequest,
  RunSimulationRequest,
  SetupStatusResponse,
  CompleteSetupRequest,
  CompleteSetupResponse,
  UserResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateProfileRequest,
  UserInfoResponse,
  HealthResponse,
  FeatureResponse,
  CreateFeatureRequest,
  TeamResponse,
  CreateTeamRequest,
  DeliverableResponse,
  CreateDeliverableRequest,
  DependencyResponse,
  MoveRequest,
  OrderResponse,
  PatchResponse,
  CachedSimulationResult,
  SimulationRunStatus,
  LicenseStatusResponse,
  UploadLicenseRequest,
  UploadLicenseResponse,
  LicenseDetailResponse,
} from '@/types/api';
import { getStoredToken, clearAuth } from '@/features/auth/utils/authStorage';
import { buildIfMatchHeader } from '@/lib/etag';

const API_BASE_URL = '/api';

/**
 * Handle authentication errors (401 Unauthorized).
 * Clears local auth state and redirects to login page.
 * Uses window.location to ensure full page reload and clear all React state.
 */
function handleAuthError(): void {
  // Clear stored auth data
  clearAuth();

  // Only redirect if not already on login or setup page
  const currentPath = window.location.pathname;
  if (currentPath !== '/login' && currentPath !== '/setup') {
    // Use full page reload to clear all React state
    window.location.href = '/login';
  }
}

/**
 * Get authorization headers if a token is stored.
 */
function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

/**
 * API error response format (following REST API design rules)
 */
interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Array<{ field: string; message: string }>;
    trace_id: string;
  };
}

/**
 * Data envelope for single resources
 */
interface DataEnvelope<T> {
  data: T;
}

/**
 * List envelope for collections
 */
interface ListEnvelope<T> {
  data: T[];
}

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public details?: Array<{ field: string; message: string }>,
    public traceId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if this is a conflict error (412 Precondition Failed).
   * This means another user modified the resource since we last fetched it.
   */
  isConflict(): boolean {
    return this.statusCode === 412;
  }
}

/**
 * Build the ApiError for a failed response.
 *
 * Reads the error envelope when the body is one. A body that is not JSON — a
 * plain-text rejection, a proxy's error page — is kept as the message, because
 * it is the only thing that says what went wrong (shipcast-894a: a dataset
 * save showed a bare "HTTP 422" while the server had named the bad field).
 * Only an empty or unrecognised body falls back to `HTTP <status>`.
 */
export async function errorFromResponse(response: Response): Promise<ApiError> {
  const text = await response.text().catch(() => '');
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return new ApiError(text.trim() || `HTTP ${response.status}`, response.status, 'UNKNOWN_ERROR');
  }
  const error = (parsed as Partial<ApiErrorResponse> | null)?.error;
  return new ApiError(
    error?.message || `HTTP ${response.status}`,
    response.status,
    error?.code ?? 'UNKNOWN_ERROR',
    error?.details,
    error?.trace_id,
  );
}

/**
 * Helper function to handle API responses that return a single resource wrapped in DataEnvelope.
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle 401 Unauthorized - token is invalid/expired
    if (response.status === 401) {
      handleAuthError();
    }

    throw await errorFromResponse(response);
  }
  const envelope = (await response.json()) as DataEnvelope<T>;
  return envelope.data;
}

/**
 * Helper function to handle API responses that return a list wrapped in ListEnvelope.
 */
async function handleListResponse<T>(response: Response): Promise<T[]> {
  if (!response.ok) {
    // Handle 401 Unauthorized - token is invalid/expired
    if (response.status === 401) {
      handleAuthError();
    }

    throw await errorFromResponse(response);
  }
  const envelope = (await response.json()) as ListEnvelope<T>;
  return envelope.data;
}

/**
 * Run an organization Monte Carlo simulation (one-off, no persistence).
 */
export async function runOrganizationSimulation(
  request: OrganizationMonteCarloRequest,
): Promise<OrganizationMonteCarloResponse> {
  const response = await fetch(`${API_BASE_URL}/organization-monte-carlo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<OrganizationMonteCarloResponse>(response);
}

/**
 * Check if the API server is available.
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/organization-monte-carlo`, {
      method: 'OPTIONS',
    });
    return response.ok || response.status === 405;
  } catch {
    return false;
  }
}

// ============================================================================
// Persistent Simulation API
// ============================================================================

/**
 * Check if the database is healthy and available.
 * Uses the health check endpoint which performs an actual database query.
 */
export async function checkDatabaseHealthy(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
    });

    if (!response.ok) {
      return false;
    }

    const envelope = (await response.json()) as DataEnvelope<HealthResponse>;
    return envelope.data.database_healthy;
  } catch {
    // Network errors or other failures mean not available
    return false;
  }
}

/**
 * Get the API version from the health endpoint.
 * Returns null if the API is unavailable or the request fails.
 */
export async function getApiVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
    });

    if (!response.ok) {
      return null;
    }

    const envelope = (await response.json()) as DataEnvelope<HealthResponse>;
    return envelope.data.version;
  } catch {
    return null;
  }
}

/**
 * List all simulations.
 */
export async function listSimulations(): Promise<SimulationSummary[]> {
  const response = await fetch(`${API_BASE_URL}/simulations`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleListResponse<SimulationSummary>(response);
}

/**
 * Get a simulation by ID.
 * ETags are included in the response and stored in React Query cache.
 */
export async function getSimulation(simulationId: string): Promise<SimulationResponse> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<SimulationResponse>(response);
}

/**
 * Create a new simulation.
 */
export async function createSimulation(request: CreateSimulationRequest): Promise<SimulationSummary> {
  const response = await fetch(`${API_BASE_URL}/simulations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<SimulationSummary>(response);
}

/**
 * Update a simulation's metadata (name, description, start_date, seed).
 * @param etag - ETag from React Query cache for optimistic concurrency control
 */
export async function updateSimulation(
  simulationId: string,
  request: UpdateSimulationRequest,
  etag?: string,
): Promise<SimulationSummary> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<SimulationSummary>(response);
}

/**
 * Delete a simulation.
 */
export async function deleteSimulation(simulationId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

/**
 * Start a simulation run asynchronously.
 * Returns 202 Accepted with run status. The simulation runs in the background
 * and results are delivered via SSE events.
 * Throws ApiError with status 409 if a run is already in progress.
 */
export async function runSimulation(simulationId: string, request: RunSimulationRequest): Promise<SimulationRunStatus> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<SimulationRunStatus>(response);
}

/**
 * Get the current run status for a simulation.
 * Used as a polling fallback when SSE is unavailable.
 */
export async function getSimulationRunStatus(simulationId: string): Promise<SimulationRunStatus> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/run/status`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<SimulationRunStatus>(response);
}

/**
 * Get cached simulation results from the last run.
 * Returns null if no cached results exist (simulation hasn't been run yet).
 */
export async function getSimulationResult(simulationId: string): Promise<CachedSimulationResult | null> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/run`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  // Return null if no cached results (404)
  if (response.status === 404) {
    return null;
  }

  return handleResponse<CachedSimulationResult>(response);
}

// ============================================================================
// Feature CRUD API
// ============================================================================

/**
 * Create a feature in a simulation.
 */
export async function createFeature(simulationId: string, request: CreateFeatureRequest): Promise<FeatureResponse> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/features`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<FeatureResponse>(response);
}

/**
 * Get a feature by ID.
 */
export async function getFeature(featureId: string): Promise<FeatureResponse> {
  const response = await fetch(`${API_BASE_URL}/features/${featureId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<FeatureResponse>(response);
}

/**
 * Update a feature (partial update).
 * ETag is optional - auto-save forms don't send it (unconditional "last write wins").
 * @param etag - Optional ETag for concurrency control (not used by auto-save)
 */
export async function updateFeature(
  featureId: string,
  request: { name?: string },
  etag?: string,
): Promise<FeatureResponse> {
  const response = await fetch(`${API_BASE_URL}/features/${featureId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<FeatureResponse>(response);
}

/**
 * Delete a feature.
 * ETag should be passed to prevent deleting a resource modified by another user.
 * @param etag - ETag for concurrency control (recommended for deletes)
 */
export async function deleteFeature(featureId: string, etag?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/features/${featureId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

// ============================================================================
// Team CRUD API
// ============================================================================

/**
 * Create a team in a simulation.
 */
export async function createTeam(simulationId: string, request: CreateTeamRequest): Promise<TeamResponse> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/teams`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<TeamResponse>(response);
}

/**
 * Get a team by ID.
 */
export async function getTeam(teamId: string): Promise<TeamResponse> {
  const response = await fetch(`${API_BASE_URL}/teams/${teamId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<TeamResponse>(response);
}

/**
 * Update a team (partial update).
 * ETag is optional - auto-save forms don't send it (unconditional "last write wins").
 * @param etag - Optional ETag for concurrency control (not used by auto-save)
 */
export async function updateTeam(
  teamId: string,
  request: Partial<CreateTeamRequest>,
  etag?: string,
): Promise<TeamResponse> {
  const response = await fetch(`${API_BASE_URL}/teams/${teamId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<TeamResponse>(response);
}

/**
 * Delete a team.
 * ETag should be passed to prevent deleting a resource modified by another user.
 * @param etag - ETag for concurrency control (recommended for deletes)
 */
export async function deleteTeam(teamId: string, etag?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/teams/${teamId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

/**
 * List deliverables for a team.
 */
export async function listTeamDeliverables(teamId: string): Promise<DeliverableResponse[]> {
  const response = await fetch(`${API_BASE_URL}/teams/${teamId}/deliverables`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleListResponse<DeliverableResponse>(response);
}

// ============================================================================
// Deliverable CRUD API
// ============================================================================

/**
 * Create a deliverable in a team.
 */
export async function createDeliverable(
  simulationId: string,
  teamId: string,
  request: CreateDeliverableRequest,
): Promise<DeliverableResponse> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/teams/${teamId}/deliverables`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<DeliverableResponse>(response);
}

/**
 * Get a deliverable by ID.
 */
export async function getDeliverable(deliverableId: string): Promise<DeliverableResponse> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<DeliverableResponse>(response);
}

/**
 * Update a deliverable (partial update).
 * ETag is optional - auto-save forms don't send it (unconditional "last write wins").
 * @param etag - Optional ETag for concurrency control (not used by auto-save)
 */
export async function updateDeliverable(
  deliverableId: string,
  request: Partial<CreateDeliverableRequest>,
  etag?: string,
): Promise<DeliverableResponse> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<DeliverableResponse>(response);
}

/**
 * Delete a deliverable.
 * ETag should be passed to prevent deleting a resource modified by another user.
 * @param etag - ETag for concurrency control (recommended for deletes)
 */
export async function deleteDeliverable(deliverableId: string, etag?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
      ...buildIfMatchHeader(etag),
    },
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

// ============================================================================
// Ordered lists (moves)
// ============================================================================

/**
 * Move one item of an ordered list to just after `afterId`, or to the top when
 * `afterId` is null. The server applies the move to the CURRENT order, so a
 * concurrent move by another user is kept, and answers with the stored order.
 */
async function postMove(path: string, id: string, afterId: string | null): Promise<string[]> {
  const body: MoveRequest = { id, after_id: afterId };
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
  });
  const order = await handleResponse<OrderResponse>(response);
  return order.ids;
}

/** Move a deliverable within its team's priority order; resolves to the stored order. */
export function postDeliverableMove(teamId: string, id: string, afterId: string | null): Promise<string[]> {
  return postMove(`/teams/${teamId}/deliverables/moves`, id, afterId);
}

/** Move a feature within its simulation's order; resolves to the stored order. */
export function postFeatureMove(simulationId: string, id: string, afterId: string | null): Promise<string[]> {
  return postMove(`/simulations/${simulationId}/features/moves`, id, afterId);
}

/** Move a team within its simulation's order; resolves to the stored order. */
export function postTeamMove(simulationId: string, id: string, afterId: string | null): Promise<string[]> {
  return postMove(`/simulations/${simulationId}/teams/moves`, id, afterId);
}

// ============================================================================
// Dependencies
// ============================================================================

/**
 * Create a dependency between deliverables.
 * @param fromDeliverableId - The deliverable that depends on another (the dependent)
 * @param toDeliverableId - The deliverable that must finish first (the predecessor)
 */
export async function createDependency(
  fromDeliverableId: string,
  toDeliverableId: string,
): Promise<DependencyResponse> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${fromDeliverableId}/dependencies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ to_deliverable_id: toDeliverableId }),
  });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  const data = (await response.json()) as DataEnvelope<DependencyResponse>;
  return data.data;
}

/**
 * Delete all dependencies from a deliverable.
 */
export async function clearDependencies(deliverableId: string): Promise<number> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}/dependencies`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  const data = (await response.json()) as DataEnvelope<{ deleted_count: number }>;
  return data.data.deleted_count;
}

/**
 * Delete a specific dependency by ID.
 */
export async function deleteDependency(dependencyId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/dependencies/${dependencyId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

/**
 * Delete a specific dependency by deliverable and predecessor IDs.
 * @param deliverableId - The deliverable that depends on another (the dependent)
 * @param predecessorId - The deliverable that must finish first (the predecessor)
 */
export async function deleteSpecificDependency(deliverableId: string, predecessorId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/deliverables/${deliverableId}/dependencies/${predecessorId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

// ============================================================================
// Excel Sheet Import/Export API
// ============================================================================

/**
 * Export a team's data as an Excel file.
 */
export async function exportTeamSheet(teamId: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/teams/${teamId}/sheet`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  return response.blob();
}

/**
 * Import a team's data from an Excel file (replace all).
 */
export async function importTeamSheet(teamId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/teams/${teamId}/sheet`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

/**
 * Merge/upsert a team's data from an Excel file.
 */
export async function patchTeamSheet(teamId: string, file: File): Promise<PatchResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/teams/${teamId}/sheet`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: formData,
  });
  return handleResponse<PatchResponse>(response);
}

/**
 * Export a simulation's data as an Excel file.
 */
export async function exportSimulationSheet(simulationId: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/sheet`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  return response.blob();
}

/**
 * Import a simulation's data from an Excel file (replace all).
 */
export async function importSimulationSheet(simulationId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/sheet`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

/**
 * Merge/upsert a simulation's data from an Excel file.
 */
export async function patchSimulationSheet(simulationId: string, file: File): Promise<PatchResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/simulations/${simulationId}/sheet`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: formData,
  });
  return handleResponse<PatchResponse>(response);
}

/**
 * Trigger a file download from a Blob.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Password Change API
// ============================================================================

/**
 * Change the authenticated user's password.
 * Requires the current password for verification.
 * Returns void on success (204 No Content).
 *
 * Note: This function handles 401 errors locally (wrong current password)
 * instead of triggering the global auth redirect.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/password`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    // Don't trigger global auth redirect for wrong current password (401)
    // — that's a user input error, not a session expiry
    throw await errorFromResponse(response);
  }
}

// ============================================================================
// Current User API
// ============================================================================

/**
 * Fetch the currently authenticated user from the backend.
 * Used to validate that the stored JWT token is still valid on app startup.
 * Returns null if no token is stored (not an error — just not logged in).
 * Throws on network errors; 401 is handled by handleResponse which calls handleAuthError.
 */
export async function fetchCurrentUser(): Promise<UserInfoResponse | null> {
  const token = getStoredToken();
  if (!token) {
    return null;
  }

  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
    },
  });
  return handleResponse<UserInfoResponse>(response);
}

// ============================================================================
// Profile Update API
// ============================================================================

/**
 * Update the authenticated user's profile (name).
 * Returns the updated user info.
 */
export async function updateProfile(request: UpdateProfileRequest): Promise<UserInfoResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<UserInfoResponse>(response);
}

// ============================================================================
// Setup API
// ============================================================================

// Types are imported from @/types/api (generated from OpenAPI spec)
// Re-export for convenience
export type { SetupStatusResponse, CompleteSetupRequest } from '@/types/api';

/**
 * Get the current setup status.
 * This endpoint does not require authentication.
 */
export async function getSetupStatus(): Promise<SetupStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/setup/status`, {
    method: 'GET',
  });
  return handleResponse<SetupStatusResponse>(response);
}

/**
 * Complete the initial setup by creating the first admin user.
 * This endpoint does not require authentication.
 * Returns auth credentials to automatically log the user in.
 */
export async function completeSetup(request: CompleteSetupRequest): Promise<CompleteSetupResponse> {
  const response = await fetch(`${API_BASE_URL}/setup/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  return handleResponse<CompleteSetupResponse>(response);
}

// ============================================================================
// User Management API (Admin only)
// ============================================================================

// Re-export types for convenience (types are defined in @/types/api)
export type { UserResponse, CreateUserRequest, UpdateUserRequest } from '@/types/api';

/**
 * List all users (admin only).
 */
export async function listUsers(): Promise<UserResponse[]> {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleListResponse<UserResponse>(response);
}

/**
 * Get a user by ID (admin only).
 */
export async function getUser(userId: string): Promise<UserResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<UserResponse>(response);
}

/**
 * Create a new user (admin only).
 */
export async function createUser(request: CreateUserRequest): Promise<UserResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<UserResponse>(response);
}

/**
 * Update a user (admin only).
 */
export async function updateUser(userId: string, request: UpdateUserRequest): Promise<UserResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse<UserResponse>(response);
}

/**
 * Delete a user (admin only).
 */
export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}

// ============================================================================
// License API
// ============================================================================

/**
 * Get the current license status.
 * This endpoint does not require authentication.
 */
export async function getLicenseStatus(): Promise<LicenseStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/license/status`, {
    method: 'GET',
  });
  return handleResponse<LicenseStatusResponse>(response);
}

/**
 * Upload a license key.
 * This endpoint does not require authentication.
 */
export async function uploadLicense(request: UploadLicenseRequest): Promise<UploadLicenseResponse> {
  const response = await fetch(`${API_BASE_URL}/license`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  return handleResponse<UploadLicenseResponse>(response);
}

/**
 * Get detailed license information (admin only).
 */
export async function getLicenseDetails(): Promise<LicenseDetailResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/license`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<LicenseDetailResponse>(response);
}

/**
 * Remove the current license (admin only).
 */
export async function deleteLicense(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/admin/license`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok && response.status !== 204) {
    throw await errorFromResponse(response);
  }
}
